// web/src/main.ts — Entry + history.pushState pathname router.
//
// D-Claude's-discretion #6: NO router library. Read window.location.pathname,
// dispatch to a screen module's mount(root, props?). Each mount returns an
// unmount fn called before the next mount.
//
// PITFALLS #5: NEVER use hash routing — iOS Safari treats hash change as
// a navigation event for mic permission state, which would re-trigger the
// OS permission sheet. history.pushState only.

import "./style.css";
import { animate } from "motion";
import type { AdminRouteProps } from "./types/api.js";

type Unmount = () => void;
type Mount = (root: HTMLElement, props?: unknown) => Unmount;
// RouteProps is the union of all typed prop shapes parseRouteProps may emit.
// Admin screens import AdminRouteProps directly and narrow by `kind`; the
// kid-app screens read their `{ sessionId | gameShortCode }` shape from the
// generic record at the call site (no behavior change for non-admin paths).
type RouteProps =
  | AdminRouteProps
  | { sessionId: string }
  | { gameShortCode: string }
  | Record<string, never>;

// Screens may install a back-press intercept by assigning a function to
// window.__onBackPress__. The popstate handler below calls it before
// dispatching; if it returns true the default URL dispatch is suppressed.
// Used by card.ts to convert browser back into pause / end-game.
declare global {
  interface Window {
    __onBackPress__?: () => boolean;
  }
}

let currentUnmount: Unmount | null = null;

async function dispatch(pathname: string, root: HTMLElement): Promise<void> {
  // BETA-02 cross-fade: 100ms fade-out before unmount + 200ms fade-in
  // after mount. Skip on initial load (no prior unmount) and skip when
  // navigating to /intro (which has its own Motion fade-in animation).
  const hasPrior = currentUnmount != null;
  const isIntro = pathname.startsWith("/intro/");

  if (hasPrior && !isIntro) {
    try { await animate(root, { opacity: [1, 0] }, { duration: 0.1, ease: "easeIn" }); } catch { /* ignore */ }
  }

  if (currentUnmount) { try { currentUnmount(); } catch { /* ignore unmount errors */ } currentUnmount = null; }
  root.replaceChildren();

  // Lazy-import screens to keep the entry bundle light. esbuild bundles them.
  let mod: { mount: Mount } | null = null;
  if      (pathname === "/" || pathname === "/login")        mod = await import("./screens/login.js");
  else if (pathname === "/picker")                            mod = await import("./screens/picker.js");
  else if (pathname === "/preflight")                         mod = await import("./screens/preflight.js");
  else if (pathname === "/permission-denied")                 mod = await import("./screens/permission_denied.js");
  else if (pathname.startsWith("/intro/"))                    mod = await import("./screens/intro.js");
  else if (pathname.startsWith("/play/"))                     mod = await import("./screens/card.js");
  else if (pathname.startsWith("/final/"))                    mod = await import("./screens/final.js");
  else if (pathname === "/privacy")                            mod = await import("./screens/privacy.js");
  else if (pathname === "/config")                             mod = await import("./screens/config.js");
  // /admin/* router branch.
  // URL hierarchy:
  //   /admin, /admin/sessions         → Game log (sessions list — restored v1 landing)
  //   /admin/sessions/:id             → session detail (attempts table)
  //   /admin/attempts/:id             → attempt detail (+ LLM/STT call panels + audio)
  //   /admin/games                    → games list
  //   /admin/games/:gc                → game edit (+ Cards panel)
  //   /admin/games/:gc/cards/:cc      → card edit (+ Tasks panel)
  //   /admin/games/:gc/cards/:cc/tasks/:ts → task edit (+ image upload + 5 inline routing slots)
  //   /admin/textsets, /admin/textsets/:sc, /admin/texts, /admin/texts/:sc
  //     → textsets/texts list + edit screens
  //   /admin/tts                      → TTS console (voice/steering settings + phrase browser)
  //   /admin/tts/logs                 → TTS cost summary + call audit log
  else if (pathname === "/admin" || pathname === "/admin/" || pathname === "/admin/sessions" || pathname === "/admin/sessions/") {
    mod = await import("./screens/admin/sessions_list.js");
  }
  else if (pathname.startsWith("/admin/sessions/")) {
    mod = await import("./screens/admin/sessions_detail.js");
  }
  else if (pathname.startsWith("/admin/attempts/")) {
    mod = await import("./screens/admin/attempts_detail.js");
  }
  else if (pathname === "/admin/settings" || pathname === "/admin/settings/") {
    mod = await import("./screens/admin/settings.js");
  }
  else if (pathname === "/admin/tts/logs" || pathname === "/admin/tts/logs/") {
    mod = await import("./screens/admin/tts_logs.js");
  }
  else if (pathname === "/admin/tts" || pathname === "/admin/tts/") {
    mod = await import("./screens/admin/tts_phrases.js");
  }
  else if (pathname === "/admin/games" || pathname === "/admin/games/") {
    mod = await import("./screens/admin/games_list.js");
  }
  else if (pathname.startsWith("/admin/games/")) {
    const segs = pathname.split("/").filter(Boolean); // ["admin","games", gc, ...]
    if (segs.length === 3) {
      mod = await import("./screens/admin/games_edit.js");
    } else if (segs.length === 5 && segs[3] === "cards") {
      mod = await import("./screens/admin/cards_edit.js");
    } else if (segs.length === 7 && segs[3] === "cards" && segs[5] === "tasks") {
      mod = await import("./screens/admin/tasks_edit.js");
    } else {
      mod = await import("./screens/admin/games_list.js");
    }
  }
  else if (pathname === "/admin/textsets" || pathname === "/admin/textsets/") {
    mod = await import("./screens/admin/textsets_list.js");
  }
  else if (pathname.startsWith("/admin/textsets/")) {
    mod = await import("./screens/admin/textsets_edit.js");
  }
  // v3.2 (migration 0038): /admin/texts routes removed; redirect to TextSets.
  else if (pathname === "/admin/texts" || pathname === "/admin/texts/" || pathname.startsWith("/admin/texts/")) {
    mod = await import("./screens/admin/textsets_list.js");
  }
  else                                                         mod = await import("./screens/login.js");

  if (mod) currentUnmount = mod.mount(root, parseRouteProps(pathname));

  if (!isIntro) {
    try {
      root.style.opacity = "0";
      await animate(root, { opacity: [0, 1] }, { duration: 0.2, ease: "easeOut" });
      root.style.opacity = "";
    } catch { /* ignore */ }
  }
}

function parseRouteProps(pathname: string): RouteProps {
  // Examples: /play/<sessionId>, /intro/<gameShortCode>, /final/<sessionId>,
  //           /admin/games/<gc>[/cards/<cc>[/tasks/<ts>]],
  //           /admin/textsets[/<sc>], /admin/texts[/<sc>]
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "play"  && parts[1]) return { sessionId: parts[1] };
  if (parts[0] === "intro" && parts[1]) return { gameShortCode: parts[1] };
  if (parts[0] === "final" && parts[1]) return { sessionId: parts[1] };

  // Typed admin route props (I-10 — no `any`). Each admin screen narrows on
  // `kind` before reading its expected fields.
  if (parts[0] === "admin") {
    // Game log — sessions / attempts (the /admin landing).
    if (parts[1] === "sessions" && parts[2]) {
      return { kind: "admin-session-detail", sessionID: parts[2] };
    }
    if (parts[1] === "sessions") {
      return { kind: "admin-sessions-list" };
    }
    if (parts[1] === "attempts" && parts[2]) {
      return { kind: "admin-attempt-detail", attemptID: parts[2] };
    }
    if (parts[1] === "settings") {
      return { kind: "admin-settings" };
    }
    if (parts[1] === "games" && parts[2]) {
      const gameShortCode = parts[2];
      if (parts[3] === "cards" && parts[4]) {
        const cardShortCode = parts[4];
        if (parts[5] === "tasks" && parts[6] === "new") {
          return { kind: "admin-task-new", gameShortCode, cardShortCode };
        }
        if (parts[5] === "tasks" && parts[6]) {
          return { kind: "admin-task-edit", gameShortCode, cardShortCode, taskShortCode: parts[6] };
        }
        return { kind: "admin-card-edit", gameShortCode, cardShortCode };
      }
      return { kind: "admin-game-edit", gameShortCode };
    }
    if (parts[1] === "games") {
      return { kind: "admin-games-list" };
    }
    if (parts[1] === "textsets" && parts[2]) {
      return { kind: "admin-textset-edit", textSetShortCode: parts[2] };
    }
    if (parts[1] === "textsets") {
      return { kind: "admin-textsets-list" };
    }
    // v3.2 (migration 0038): /admin/texts routes removed. Anyone landing
    // there is bumped to the TextSets list.
    if (parts[1] === "texts") {
      return { kind: "admin-textsets-list" };
    }
    // TTS console. ?voice= and ?page= are read from location.search by the
    // screens themselves, so the route props carry no fields.
    if (parts[1] === "tts" && parts[2] === "logs") {
      return { kind: "admin-tts-logs" };
    }
    if (parts[1] === "tts") {
      return { kind: "admin-tts-phrases" };
    }
    // /admin (no further segments) → Game log (restored v1 landing).
    return { kind: "admin-sessions-list" };
  }
  return {};
}

export function navigate(to: string): void {
  history.pushState({}, "", to);
  const root = document.getElementById("app");
  if (root) dispatch(to, root);
}

window.addEventListener("popstate", () => {
  const intercept = window.__onBackPress__;
  if (intercept && intercept() === true) return;
  const root = document.getElementById("app");
  if (root) dispatch(window.location.pathname, root);
});

const root = document.getElementById("app");
if (root) dispatch(window.location.pathname, root);
